import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { CSVUploader } from '@/components/CSVUploader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DataPagination } from '@/components/DataPagination';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

// Configuration for sortable columns
// Add new columns here to make them sortable
const SORTABLE_COLUMNS: { [key: string]: boolean } = {
  'product_name': true,
  'created_at': true,
  'rank': true,
  'bmc_comp_score': true,
  // Add more sortable columns here in the future
};

export default function HiddenGems() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Fetch distinct upload_date values for the date dropdown
  const { data: dateOptions } = useQuery({
    queryKey: ['hidden-gems-dates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hidden_gems')
        .select('upload_date')
        .order('upload_date', { ascending: false });
      if (error) throw error;
      const unique = Array.from(new Set((data ?? []).map((r: any) => r.upload_date).filter(Boolean)));
      return unique as string[];
    },
  });
  console.log("🚀 ~ HiddenGems ~ dateOptions:", dateOptions)

  const { data, isLoading, refetch } = useQuery({
  queryKey: [
    'hidden-gems',
    currentPage,
    pageSize,
    search,
    sortConfig?.key,
    sortConfig?.direction,
    selectedDate,
  ],
  queryFn: async () => {
    let query = supabase
      .from('hidden_gems')
      .select('*', { count: 'exact' });

    if (search && search.trim() !== '') {
      query = query.ilike('product_name', `%${search}%`);
    }

    if (selectedDate) {
      query = query.eq('upload_date', selectedDate);
    }

    if (sortConfig) {
      query = query.order(sortConfig.key, {
        ascending: sortConfig.direction === 'asc',
      });
    }

    // ✅ Pagination
    query = query.range(
      (currentPage - 1) * pageSize,
      currentPage * pageSize - 1
    );

    const { data, count, error } = await query;

    if (error) throw error;

    return {
      data: data ?? [],
      total: count ?? 0,
    };
  },
});
  console.log("🚀 ~ HiddenGems ~ data:", data)

  // Get column headers from first row
  const columns = useMemo(() => {
    if (!data || data.data.length === 0) return [];
    return Object.keys(data.data[0]);
  }, [data]);

  // Filter and sort data
  const filteredAndSortedGems = useMemo(() => {
    console.log("🚀 ~ HiddenGems ~ filteredAndSortedGems ~ data:", data)
    if (!data || data.data.length === 0) return [];

    let filtered = data.data;

    // Apply search filter
    // if (search.trim()) {
    //   const searchLower = search.toLowerCase();
    //   filtered = data.data.filter((gem: any) => {
    //     return Object.values(gem).some((value) =>
    //       String(value ?? '').toLowerCase().includes(searchLower)
    //     );
    //   });
    // }

    // Apply sorting
  //   if (sortConfig) {
  //     filtered = [...filtered].sort((a: any, b: any) => {
  //       const aValue = a[sortConfig.key];
  //       const bValue = b[sortConfig.key];

  //       if (aValue === null || aValue === undefined) return 1;
  //       if (bValue === null || bValue === undefined) return -1;

  //       const aString = String(aValue).toLowerCase();
  //       const bString = String(bValue).toLowerCase();

  //       if (aString < bString) {
  //         return sortConfig.direction === 'asc' ? -1 : 1;
  //       }
  //       if (aString > bString) {
  //         return sortConfig.direction === 'asc' ? 1 : -1;
  //       }
  //       return 0;
  //     });
  //   }

    return filtered;
  }, [data]);

  // Paginate data
  // const paginatedGems = useMemo(() => {
  //   const startIndex = (currentPage - 1) * pageSize;
  //   const endIndex = startIndex + pageSize;
  //   return filteredAndSortedGems.slice(startIndex, endIndex);
  // }, [filteredAndSortedGems, currentPage, pageSize]);

  const totalPages = Math.ceil(data?.total / pageSize);

  const handleExport = async () => {
    setExporting(true);
    try {
      const BATCH = 1000;
      let allData: any[] = [];
      let from = 0;

      while (true) {
        let query = supabase
          .from('hidden_gems')
          .select('*')
          .range(from, from + BATCH - 1);

        if (search && search.trim() !== '') {
          query = query.ilike('product_name', `%${search}%`);
        }

        if (selectedDate) {
          query = query.eq('upload_date', selectedDate);
        }

        if (sortConfig) {
          query = query.order(sortConfig.key, {
            ascending: sortConfig.direction === 'asc',
          });
        }

        const { data: batch, error } = await query;
        if (error) throw error;
        if (!batch || batch.length === 0) break;

        allData = allData.concat(batch);
        if (batch.length < BATCH) break;
        from += BATCH;
      }

      if (allData?.length === 0) return;

      const headers = Object.keys(allData[0]);
      const csvRows = [
        headers.join(','),
        ...allData.map((row: any) =>
          headers
            .map((h) => {
              const val = String(row[h] ?? '');
              return val.includes(',') || val.includes('"') || val.includes('\n')
                ? `"${val.replace(/"/g, '""')}"`
                : val;
            })
            .join(',')
        ),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hidden_gems_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Check if a column is sortable
  const isSortable = (key: string) => SORTABLE_COLUMNS[key] === true;

  // Handle sort
  const handleSort = (key: string) => {
    // Only sort if column is sortable
    if (!isSortable(key)) return;
    
    setSortConfig((current) => {
      if (!current || current.key !== key || current.direction === 'desc') {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  // Get sort icon for column
  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Hidden Gems Manager</h1>
      <Card>
        <CardHeader><CardTitle className="text-xl sm:text-2xl">Upload CSV</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <CSVUploader tableName="hidden_gems" onComplete={() => refetch()} conflictKeys={['product_name', 'upload_date']} />
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-sm font-medium">Required columns for uploaded file</p>
            <p className="mt-2 text-xs text-muted-foreground break-words">
              id, product_name, product_status, store_name, country_region, product_category, selling_price, commission_rate, sales_volume_7d, gmv_7d, total_sales_volume, total_gmv, gmv_ratio_7d_total, gmv_per_creator, total_creators, creator_sales_rate, total_promo_videos, total_promo_livestreams, bmc_trending_score, bmc_comp_score, bmc_overall_rating, rank, product_image_url, fastmoss_url, tiktok_url, fastmoss_shop_url, mike_notes, gmv_bonus, upload_date, created_at
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-xl sm:text-2xl">Current Data ({filteredAndSortedGems?.length} rows)</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={selectedDate ?? 'all'}
                onValueChange={(v) => {
                  setSelectedDate(v === 'all' ? null : v);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  {(dateOptions ?? [])?.map((d) => (
                    <SelectItem key={d} value={d}>
                      {new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search product..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 w-full sm:w-[300px]"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting || isLoading || !data?.total}
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns?.map((key) => (
                    <TableHead key={key} className="whitespace-nowrap">
                      {isSortable(key) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent"
                          onClick={() => handleSort(key)}
                        >
                          <span>{key}</span>
                          {getSortIcon(key)}
                        </Button>
                      ) : (
                        // No px-3: the sortable variant's Button carries px-3
                        // cancelled by -ml-3, so its label sits flush with the
                        // cell text. Padding here would offset this one by 12px.
                        <span>{key}</span>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length || 1}
                      className="h-24 text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredAndSortedGems?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center text-muted-foreground"
                    >
                      {search ? 'No results found.' : 'No data available.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedGems?.map((row: any, i: number) => (
                    <TableRow key={row.id ?? i}>
                      {columns.map((key, j) => (
                        <TableCell key={j} className="max-w-[200px] truncate">
                          {String(row[key] ?? '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filteredAndSortedGems?.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={data?.total}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
